import { type FormEvent, useEffect, useState } from "react";
import { AdminPageHeader } from "../../components/admin/AdminPageHeader";
import { AdminPanel } from "../../components/admin/AdminPanel";
import { FeatureDisabledState } from "../../components/trainer/FeatureDisabledState";
import { Button } from "../../components/ui/Button";
import { EmptyState } from "../../components/ui/EmptyState";
import { FullPageLoader } from "../../components/ui/FullPageLoader";
import { Input } from "../../components/ui/Input";
import { useBusinessProfile } from "../../hooks/useBusinessProfile";
import { useTenant } from "../../hooks/useTenant";
import { useWorkspaceFeatures } from "../../hooks/useWorkspaceFeatures";
import { businessProfileService } from "../../services/businessProfileService";

export function TrainerBusinessPage() {
  const { currentTenant, currentRole } = useTenant();
  const { data: features } = useWorkspaceFeatures(currentTenant?.id);
  const { data, loading, error, refresh } = useBusinessProfile(currentTenant?.id);
  const [form, setForm] = useState({
    business_name: "",
    public_display_name: "",
    contact_email: "",
    contact_phone: "",
    website_url: "",
    brand_colour: "",
    timezone: "Europe/London",
    default_check_in_day: "",
    default_client_invite_message: "",
  });
  const [saving, setSaving] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const canEdit = currentRole === "tenant_owner";

  useEffect(() => {
    if (!data) return;
    setForm({
      business_name: data.business_name,
      public_display_name: data.public_display_name ?? "",
      contact_email: data.contact_email ?? "",
      contact_phone: data.contact_phone ?? "",
      website_url: data.website_url ?? "",
      brand_colour: data.brand_colour ?? "",
      timezone: data.timezone,
      default_check_in_day: data.default_check_in_day ?? "",
      default_client_invite_message: data.default_client_invite_message ?? "",
    });
  }, [data]);

  async function save(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!currentTenant) return;
    setSaving(true);
    setActionError(null);
    try {
      await businessProfileService.update(currentTenant.id, {
        ...form,
        brand_colour: form.brand_colour || null,
        default_check_in_day: form.default_check_in_day || null,
        default_client_invite_message: form.default_client_invite_message || null,
      });
      await refresh();
    } catch (caughtError) {
      setActionError(caughtError instanceof Error ? caughtError.message : "Unable to save business profile.");
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <FullPageLoader label="Loading business profile" />;
  if (error) return <EmptyState title="Unable to load business profile.">{error}</EmptyState>;
  if (features && !features.gym_profiles_enabled) {
    return <FeatureDisabledState title="Business profiles are not enabled." />;
  }

  return (
    <div>
      <AdminPageHeader title="Business Profile" description="Workspace identity and default client-facing contact details." />
      <AdminPanel title={canEdit ? "Edit business profile" : "Business profile"}>
        {!canEdit ? <p className="mb-5 text-sm text-graphite/70">Only workspace owners can edit these settings.</p> : null}
        {actionError ? <div className="mb-5 rounded-md border border-red-900/20 bg-red-50 p-4 text-sm text-red-900">{actionError}</div> : null}
        <form className="grid gap-4 lg:grid-cols-2" onSubmit={save}>
          <Input disabled={!canEdit} label="Business name" value={form.business_name} onChange={(event) => setForm({ ...form, business_name: event.target.value })} required />
          <Input disabled={!canEdit} label="Public display name" value={form.public_display_name} onChange={(event) => setForm({ ...form, public_display_name: event.target.value })} />
          <Input disabled={!canEdit} label="Contact email" type="email" value={form.contact_email} onChange={(event) => setForm({ ...form, contact_email: event.target.value })} />
          <Input disabled={!canEdit} label="Contact phone" value={form.contact_phone} onChange={(event) => setForm({ ...form, contact_phone: event.target.value })} />
          <Input disabled={!canEdit} label="Website URL" value={form.website_url} onChange={(event) => setForm({ ...form, website_url: event.target.value })} />
          <Input disabled={!canEdit} label="Brand colour" value={form.brand_colour} onChange={(event) => setForm({ ...form, brand_colour: event.target.value })} />
          <Input disabled={!canEdit} label="Timezone" value={form.timezone} onChange={(event) => setForm({ ...form, timezone: event.target.value })} />
          <Input disabled={!canEdit} label="Check-in day" value={form.default_check_in_day} onChange={(event) => setForm({ ...form, default_check_in_day: event.target.value })} />
          <label className="block text-sm font-medium text-ink lg:col-span-2">
            Default invite message
            <textarea disabled={!canEdit} className="mt-2 min-h-28 w-full rounded-md border border-line bg-field px-3 py-2" value={form.default_client_invite_message} onChange={(event) => setForm({ ...form, default_client_invite_message: event.target.value })} />
          </label>
          {canEdit ? <Button className="lg:col-span-2" isLoading={saving} type="submit">Save business profile</Button> : null}
        </form>
      </AdminPanel>
    </div>
  );
}
